import { motion } from "framer-motion";
import { NavLink } from "react-router-dom";
import {
  CalendarDays,
  GraduationCap,
  Wallet,
  BookOpen,
  Clock3,
  Globe,
  ArrowRight,
  Download,
  DockIcon,
  FlaskConical,
  Handshake,
  Award,
  FileText,
  ClipboardCheck,
  Info,
  BookCheck,
} from "lucide-react";

import DataFetcher from "../../hooks/DataFetcherPosgrados";
import Breadcumbs from "../../components/Breadcumbs";
import Accordion from "../../components/Accordion";
import BannerGeneral from "../../components/BannerGeneral";
import ConectUs from "../../components/ConectUs";
import Quote from "../../components/Quote";
import Modalidad from "../../components/posgrados/Modalidad";
import Duracion from "../../components/posgrados/Duracion";
import Campo from "../../components/posgrados/Campo";
import BlockRightImage from "../../components/BlockRightImage";
import PosgradoPagos from "../../components/PosgradoPagos";

const url =
  "https://be-ofertas.utpl.edu.ec/jsonapi/node/posgrado/9defb534-b769-4b59-9d1e-f7354dac0aae" +
  // 1. INCLUDES: Se define la ruta completa hacia el archivo físico de la imagen del docente
  "?include=field_imagen_portada.field_media_image," +
  "field_imagen_requisitos.field_media_image," +
  "field_imagen_perfil_de_egreso.field_media_image," +
  "field_imagen_perfil_ingreso.field_media_image," +
  "field_imagen_director.field_media_image," +
    "field_imagen1.field_media_image," +
  "field_nuestros_docentes.field_imagen_docente.field_media_image,field_brochure,field_archivo_malla_curricular" + // <-- Actualizado
  // 2. FIELDS [node--posgrado]: Campos del nodo principal (posgrado en singular)
  "&fields[node--posgrado]=title,field_titulo,field_modalidad,field_precio," +
  "field_campo_amplio,field_duracion,field_inicio_clases,field_resolucion," +
  "field_diferencia_1,field_diferencia_2,field_diferencia_3," +
  "field_perfil_de_ingreso,field_perfil_de_ingreso_amplio,field_perfil_de_egreso,field_perfil_de_egreso_amplio,field_atributo_1,field_atributo_2,field_atributo_3," +
  "field_texto_director,field_imagen_director,field_nuestros_docentes,field_imagen1," +
  "field_imagen_portada,field_imagen_requisitos,field_requisitos_de_postulacion," +
  "field_malla_curricular,field_imagen_perfil_de_egreso,field_imagen_perfil_ingreso,field_texto_portada," +
  "field_publicado,field_enlace_postulacion,field_brochure,field_archivo_malla_curricular" +
  // 3. FIELDS [node--docente]: Campos exactos que me indicaste
  "&fields[node--docente]=title,field_imagen_docente,field_contenido" +
  // 4. FIELDS de soporte para extraer la URI del archivo
  "&fields[media--image]=field_media_image" +
  "&fields[file--file]=uri";

export default function MaestriaEducacionMatematica() {
  const fadeUp = {
    initial: { opacity: 0, y: 40 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.6 },
  };

  const DataDisplay = ({ data }) => {
    const infoCards = [
      {
        icon: GraduationCap,
        title: "Título",
        value: data.titulo_recibir,
      },
      {
        icon: Globe,
        title: "Modalidad",
        value: <Modalidad value={data.modalidad} />,
      },
      {
        icon: Clock3,
        title: "Duración",
        value: <Duracion value={data.duracion} />,
      },
      {
        icon: BookOpen,
        title: "Campo amplio",
        value: <Campo value={data.campo} />,
      },
      {
        icon: Wallet,
        title: "Inversión",
        value: data.precio,
      },
      {
        icon: CalendarDays,
        title: "Inicio",
        value: data.inicio,
      },
    ];

    return (
      <div className="bg-white overflow-hidden mt-16">
        {/* HERO */}
        <section className="relative min-h-[90vh] flex items-center overflow-hidden">
          <img
            src={data.imagen_portada}
            alt="Portada"
            className="absolute inset-0 w-full h-full object-cover"
          />

          <div className="absolute inset-0 bg-[linear-gradient(120deg,rgba(0,66,112,0.95)_0%,rgba(0,66,112,0.6)_45%,rgba(2,6,23,0.2)_100%)]" />
          <div className="relative z-10 max-w-7xl mx-auto px-6 py-24">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="max-w-3xl"
            >
              <div
                className="[&_h2]:text-white [&_h2]:text-3xl [&_h2]:md:text-4xl [&_h2]:font-bold [&_h2]:leading-tight  [&_p]:text-xl [&_p]:text-slate-200"
                dangerouslySetInnerHTML={{
                  __html: data.field_texto_portada?.processed ?? "",
                }}
              />

              <div className="flex flex-wrap gap-4 mt-10">
                {data.field_publicado && (
                  <a
                    href={data.field_enlace_postulacion?.uri}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-amber-400 hover:bg-blue-700 hover:text-white text-[#004270] px-8 py-4 rounded-2xl font-semibold transition-all flex items-center gap-2 shadow-2xl"
                  >
                    Postular ahora
                    <ArrowRight size={18} />
                  </a>
                )}
                {data.brochure && (
                  <a
                    href={data.brochure}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-white/10 backdrop-blur-md border border-white/20 text-white px-8 py-4 rounded-2xl flex items-center gap-2 font-semibold hover:bg-white/20 transition"
                  >
                    Descargar brochure
                    <Download size={18} />
                  </a>
                )}
              </div>
            </motion.div>
          </div>
        </section>

        {/* BREADCRUMBS */}
        <div className="border-b border-slate-200 bg-white">
          <div className="mb-6">
            <Breadcumbs
              content={
                <div className="flex items-center gap-2 text-sm">
                  <NavLink to="/posgrados">Posgrados</NavLink>
                  <span>/</span>
                  <NavLink to="/maestria-educacion-matematica">
                    Maestría en Educación, Mención en Enseñanza de la Matemática
                  </NavLink>
                </div>
              }
            />
          </div>
        </div>

        {/* INFO CARDS DATA */}
        <section className="py-16 bg-slate-50">
          <div className="max-w-6xl mx-auto ">
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6 gap-6">
              {infoCards
                .filter((item) => item.value)
                .map((item, index) => {
                  const Icon = item.icon;
                  return (
                    <motion.div
                      key={index}
                      {...fadeUp}
                      transition={{
                        duration: 0.5,
                        delay: index * 0.1,
                      }}
                      whileHover={{
                        y: -8,
                      }}
                      className="
                bg-white
                rounded-3xl
                p-8
                shadow-sm
                border
                border-slate-200
                hover:shadow-2xl
                transition-all
              "
                    >
                      <div className="w-14 h-14 rounded-2xl bg-blue-100 flex items-center justify-center mb-6">
                        <Icon className="text-blue-600" size={28} />
                      </div>

                      <p className="text-sm text-slate-500">{item.title}</p>

                      <h3 className="font-bold text-lg text-slate-800 leading-snug">
                        {item.value}
                      </h3>
                    </motion.div>
                  );
                })}
            </div>
          </div>
        </section>
        {/* RESOLUCIÓN */}
        <section className="py-8 px-8 lg:px-0">
          <div className="max-w-6xl mx-auto ">
            <div className="grid grid-cols-1 md:grid-cols-2 items-center gap-8">
              <div>
                <div className="w-14 h-14 rounded-2xl bg-blue-100 flex items-center justify-center mb-6">
                  <DockIcon className="text-blue-600" size={28} />
                </div>
                <motion.div
                  {...fadeUp}
                  className="prose prose-lg max-w-none"
                  dangerouslySetInnerHTML={{
                    __html: data.resolucion,
                  }}
                />
              </div>
              {data.imagen1 && (
                <div>
                  <img
                    src={data.imagen1}
                    alt="utpl"
                    className="w-full max-w-sm object-contain rounded-lg"
                  />
                </div>
              )}
            </div>

            {data.field_publicado && (
              <div className="mt-6">
                <a
                  href="#requisitos"
                  className="inline-flex items-center text-blue-600 font-medium hover:underline"
                >
                  Conoce los requisitos de postulación →
                </a>
              </div>
            )}
          </div>
        </section>

        {/* PERFIL INGRESO */}
        <section className="py-24 bg-slate-50">
          <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-20 items-start py-4">
            <motion.div {...fadeUp}>
              <div className="[&_p]:text-sm mb-12 [&_p]:text-slate-500">
                {data.info1 && (
                  <Quote
                    content={
                      <div
                        dangerouslySetInnerHTML={{
                          __html: data.info1,
                        }}
                      />
                    }
                  ></Quote>
                )}
              </div>{" "}
              <div className="px-8 lg:px-0">
                <span className="text-blue-600 font-bold uppercase tracking-[0.2em]">
                  Perfil académico
                </span>
                <div
                  className="prose prose-lg max-w-none      
                [&_h2]:text-4xl
                [&_h2]:font-black
                [&_h2]:text-slate-900
                [&_h2]:leading-tight "
                  dangerouslySetInnerHTML={{
                    __html: data.ingreso,
                  }}
                />
                {data.ingreso_amplio && (
                  <div className="mt-4">
                    <Accordion
                      title="Más información"
                      content={
                        <div
                          dangerouslySetInnerHTML={{
                            __html: data.ingreso_amplio,
                          }}
                        />
                      }
                    />
                  </div>
                )}{" "}
              </div>
            </motion.div>

            <motion.div {...fadeUp}>
              <img
                src={data.imagen_ingreso}
                alt="Perfil"
                className="w-full h-[500px] object-cover rounded-3xl shadow-2xl"
              />
            </motion.div>
          </div>
        </section>

        {/* PERFIL EGRESO */}
        <section className="pb-24 bg-slate-50">
          <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-20 items-start py-4">
            <motion.div {...fadeUp}>
              <img
                src={data.imagen_perfil}
                alt="Perfil"
                className="w-full h-[500px] object-cover rounded-3xl shadow-2xl"
              />
            </motion.div>
            <motion.div {...fadeUp}>
              <div className="[&_p]:text-sm mb-12 [&_p]:text-slate-500">
                {data.info2 && (
                  <Quote
                    content={
                      <div
                        dangerouslySetInnerHTML={{
                          __html: data.info2,
                        }}
                      />
                    }
                  ></Quote>
                )}
              </div>
              <div className="px-8 lg:px-0">
                <span className=" text-blue-600 font-bold uppercase tracking-[0.2em]   ">
                  Perfil profesional
                </span>
                <div
                  className="prose prose-lg max-w-none  [&_h2]:text-4xl  [&_h2]:font-black  [&_h2]:text-slate-900 [&_h2]:leading-tight  "
                  dangerouslySetInnerHTML={{
                    __html: data.egreso,
                  }}
                />
                {data.egreso_amplio && (
                  <div className="mt-4">
                    <Accordion
                      title="Más información"
                      content={
                        <div
                          dangerouslySetInnerHTML={{
                            __html: data.egreso_amplio,
                          }}
                        />
                      }
                    />
                  </div>
                )}{" "}
              </div>
            </motion.div>
          </div>
        </section>

        {/* DIFERENCIALES */}
        <section className="relative py-32 bg-[#004270]  overflow-hidden">
          {/* DECORACIÓN */}
          <div className="absolute top-0 right-0 w-1/2 h-full bg-cyan-400/5 skew-x-12 translate-x-24" />

          <div className="absolute -top-40 -left-40 w-[500px] h-[500px] bg-cyan-500/10 rounded-full blur-3xl" />

          <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-blue-500/10 rounded-full blur-3xl" />

          <div className="relative z-10 max-w-7xl mx-auto px-6">
            {/* HEADER */}
            <motion.div {...fadeUp} className="text-center mb-10">
              <span className=" uppercase  tracking-[0.25em] text-amber-400 font-semibold ">
                Diferenciales
              </span>

              <h2 className=" text-3xl md:text-4xl font-black text-white p-0 m-0 leading-tight ">
                ¿Qué nos diferencia?
              </h2>
            </motion.div>

            {/* CARDS */}
            <div className="grid md:grid-cols-3 gap-8">
              {[
                {
                  icon: BookCheck,
                  content: data.diferencia1,
                },
                {
                  icon: Handshake,
                  content: data.diferencia2,
                },
                {
                  icon: Award,
                  content: data.diferencia3,
                },
              ].map((item, index) => {
                const Icon = item.icon;

                return (
                  <motion.div
                    key={index}
                    {...fadeUp}
                    transition={{
                      duration: 0.5,
                      delay: index * 0.15,
                    }}
                    whileHover={{
                      y: -12,
                      scale: 1.02,
                    }}
                    className="group  relative  overflow-hidden bg-white/5 backdrop-blur-xl  border  border-white/10 rounded-[2rem]  p-10 shadow-2xl hover:border-cyan-400/30 transition-all"
                  >
                    {/* Glow */}
                    <div className=" absolute inset-0 bg-gradient-to-br from-cyan-400/0 to-blue-500/0 group-hover:from-cyan-400/10  group-hover:to-blue-500/10 transition-all duration-700 " />

                    {/* ICONO */}
                    <div className="relative  z-10  w-20 h-20 rounded-3xl bg-white/10 flex items-center justify-center mb-4  border border-white/10 group-hover:scale-110 transition-transform duration-500">
                      <Icon size={30} className="text-amber-400" />
                    </div>

                    {/* CONTENIDO */}
                    <div
                      className="relative z-10  [&_p]:text-slate-200 [&_p]:text-lg  [&_p]:leading-relaxed [&_strong]:text-white  [&_ul]:space-y-3  "
                      dangerouslySetInnerHTML={{
                        __html: item.content,
                      }}
                    />
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* MALLA */}
        <section className="py-24 bg-gradient-to-b from-white to-gray-50">
          <div className="max-w-6xl mx-auto pt-6">
            <motion.div
              {...fadeUp}
              className="relative overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-xl"
            >
              {/* Decoración */}
              <div className="absolute top-0 right-0 w-72 h-72 bg-red-100 rounded-full blur-3xl opacity-30 -translate-y-20 translate-x-20" />
              <div className="relative z-10 pt-8 px-16">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 mb-10">
                  <div className="flex items-start gap-4">
                    <div className="bg-red-600 text-white p-4 rounded-2xl shadow-lg">
                      <BookOpen size={32} />
                    </div>

                    <div>
                      <h2 className="text-2xl md:text-3xl font-bold text-gray-900">
                        Malla Curricular
                      </h2>
                    </div>
                  </div>
                </div>

                {/* Contenido */}
                <div className="grid lg:grid-cols-12 gap-10 items-start">
                  {/* Texto */}
                  <div className="lg:col-span-6">
                    <div
                      className="
                prose prose-lg max-w-none
                prose-headings:text-gray-900
                prose-p:text-gray-700
                prose-li:text-gray-700
                prose-strong:text-gray-900
              "
                      dangerouslySetInnerHTML={{
                        __html: data.malla,
                      }}
                    />
                  </div>

                  {/* Card lateral */}
                  <motion.div whileHover={{ y: -4 }} className="lg:col-span-6">
                    {data.malla_curricular && (
                      <div className="rounded-3xl border border-gray-200 bg-gray-50 p-8 shadow-sm">
                        <div className="bg-white w-12 h-12 rounded-2xl flex items-center justify-center shadow-md mb-4">
                          <FileText className="text-red-600" size={30} />
                        </div>

                        <h3 className="text-lg font-bold text-gray-900 m-0 p-0">
                          Información académica
                        </h3>

                        <p className="text-gray-600 text-sm leading-relaxed mb-3">
                          Descarga la malla curricular completa y revisa las
                          materias, módulos y planificación académica del
                          programa.
                        </p>

                        <a
                          href={data.malla_curricular}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="group inline-flex items-center gap-3 text-red-600 font-semibold hover:text-red-700 transition"
                        >
                          Descargar documento
                          <Download
                            size={18}
                            className="group-hover:translate-y-0.5 transition-transform"
                          />
                        </a>
                      </div>
                    )}
                    <div className="mt-10 max-w-2xl [&_p]:text-sm mb-12 [&_p]:text-slate-500">
                      {data.info3 && (
                        <Quote
                          content={
                            <div
                              dangerouslySetInnerHTML={{
                                __html: data.info3,
                              }}
                            />
                          }
                        ></Quote>
                      )}
                    </div>
                  </motion.div>
                </div>
              </div>{" "}
            </motion.div>
          </div>
        </section>

        {/* DIRECTOR */}
        {data.director && (
          <section className="py-24 bg-white">
            <div className="max-w-6xl mx-auto  grid lg:grid-cols-2 gap-20 items-center">
              <motion.div {...fadeUp}>
                <img
                  src={data.imagen_director}
                  alt="Director"
                  className="w-full h-[550px] object-cover rounded-3xl shadow-2xl"
                />
              </motion.div>

              <motion.div className="px-10 lg:px-0" {...fadeUp}>
                <span className="text-blue-600 font-semibold uppercase tracking-widest">
                  Director del programa
                </span>

                <div
                  className="prose prose-lg mt-6 cta-link  "
                  dangerouslySetInnerHTML={{
                    __html: data.director,
                  }}
                />
              </motion.div>
            </div>
          </section>
        )}

        {/* DOCENTES */}
        {data.nuestros_docentes && data.nuestros_docentes.length > 0 && (
          <section className="pt-10 pb-10">
            <div className="max-w-6xl mx-auto pb-10  ">
              <motion.div {...fadeUp} className="text-center mb-10">
                <h2 className="text-4xl font-black m-0 p-0">
                  Nuestros docentes
                </h2>
              </motion.div>

              <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-8">
                {data.nuestros_docentes.map((profe, index) => (
                  <motion.div
                    key={profe.id}
                    {...fadeUp}
                    transition={{
                      duration: 0.5,
                      delay: index * 0.1,
                    }}
                    whileHover={{
                      y: -10,
                    }}
                    className="bg-white rounded-3xl overflow-hidden border border-slate-200 shadow-sm hover:shadow-2xl transition-all"
                  >
                    <img
                      src={profe.foto}
                      alt={profe.nombre}
                      className="w-full h-80 object-cover"
                    />

                    <div className="px-8">
                      <h3 className="text-lg font-bold text-slate-800">
                        {profe.nombre}
                      </h3>

                      <div
                        className=" text-slate-600 link-with-arrow-1"
                        dangerouslySetInnerHTML={{
                          __html: profe.contenido,
                        }}
                      />
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/*REQUISITOS */}
        <section
          id="requisitos"
          className="max-w-6xl mx-auto rounded-2xl border border-slate-200 bg-white p-6 shadow-sm"
        >
          <div className="px-12">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-sky-100 text-sky-600">
              <ClipboardCheck className="h-6 w-6" />
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900">
              Requisitos de postulación
            </h2>
            <div
              className="prose prose-lg max-w-none prose-headings:text-slate-900 prose-p:text-slate-700 prose-p:leading-7 prose-strong:text-slate-900 prose-strong:font-semibold prose-ul:space-y-3 prose-ul:list-disc prose-ul:pl-6 prose-li:text-slate-700 prose-li:marker:text-sky-500 prose-a:text-sky-600 prose-a:font-semibold prose-a:underline prose-a:underline-offset-4 prose-a:decoration-sky-400 prose-a:transition-colors prose-a:duration-200 hover:prose-a:text-sky-800 hover:prose-a:decoration-sky-700 [&_h2]:mb-4 [&_h2]:text-2xl [&_h2]:font-bold [&_h3]:mt-6 [&_h3]:text-xl [&_h3]:font-semibold [&_blockquote]:border-l-4 [&_blockquote]:border-sky-400 [&_blockquote]:bg-sky-50 [&_blockquote]:px-4 [&_blockquote]:py-2 [&_blockquote]:italic"
              dangerouslySetInnerHTML={{
                __html: data.requisitos,
              }}
            />{" "}
          </div>
          {data.field_publicado && (
            <div>
              <div className="flex mt-10 px-12">
                <a
                  href={data.field_enlace_postulacion?.uri}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-amber-400 hover:bg-blue-700 hover:text-white text-[#004270] px-8 py-4 rounded-2xl font-semibold transition-all flex items-center gap-2 shadow-2xl"
                >
                  Postular ahora
                  <ArrowRight size={18} />
                </a>
              </div>
              <div className="mt-10">
                <PosgradoPagos />
              </div>
            </div>
          )}
        </section>
      </div>
    );
  };

  return (
    <>
      <DataFetcher url={url}>
        {(data) => <DataDisplay data={data} />}
      </DataFetcher>
      <div className="max-w-6xl m-auto">
        <div className="mt-8 rounded-2xl border border-sky-200 bg-sky-50 p-5 flex gap-3 items-start">
          <Info className="h-5 w-5 text-sky-600 mt-1" />

          <p className="text-slate-700 text-xs leading-7 p-0">
            Implementamos adaptaciones curriculares no significativas para
            garantizar experiencias de aprendizaje inclusivas y accesibles.
          </p>
        </div>
      </div>
      <div className="mt-10 py-8">
        <ConectUs
          lema="Descubre contenido exclusivo y mantente conectado con nuestra comunidad."
          instagram="https://www.instagram.com/utplposgrados/"
          facebook="https://www.facebook.com/utplposgrados"
        />
      </div>

      <BannerGeneral />
    </>
  );
}
